import React, { useEffect, useState } from 'react';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { toast } from 'react-toastify';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';


const HREditIP = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    strengths: '',
    weaknesses: '',
    employee_comment: '',
    training_needs: '',
    employee_signature: null
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const baseUrl = API_BASE_URL;

  useEffect(() => {
    const fetchPlan = async () => {
      setInitialLoading(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/improvement-plans/${id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = response.data;
        setFormData({
          strengths: data?.strengths || '',
          weaknesses: data?.weaknesses || '',
          employee_comment: data?.employee_comment || '',
          training_needs: data?.training_needs || '',
          employee_signature: null
        });
      } catch (error) {
        console.error('Error fetching improvement plan:', error);
        toast.error(error.response?.data?.detail || 'Failed to load improvement plan');
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchPlan();
    }
  }, [baseUrl, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.strengths.trim()) {
      newErrors.strengths = 'Strengths are required';
    }
    if (!formData.weaknesses.trim()) {
      newErrors.weaknesses = 'Weaknesses are required';
    }
    if (!formData.employee_comment.trim()) {
      newErrors.employee_comment = 'Employee comment is required';
    }
    if (!formData.training_needs.trim()) {
      newErrors.training_needs = 'Training needs are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = Cookies.get('token');

      const submitData = new FormData();
      submitData.append('strengths', formData.strengths);
      submitData.append('weaknesses', formData.weaknesses);
      submitData.append('employee_comment', formData.employee_comment);
      submitData.append('training_needs', formData.training_needs);

      await axios.put(
        `${baseUrl}/api/improvement-plans/${id}/`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Improvement plan updated successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Error updating improvement plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to update improvement plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Edit Improvement Plan</h4>
              <p className="text-muted mb-0 fs-13">Update the details for this improvement plan</p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left" /> Back
            </button>
          </div>

          {/* Form Card */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-light border-bottom py-3">
              <h6 className="card-title mb-0 fw-bold text-dark">Plan Details</h6>
            </div>
            <div className="card-body">
              {initialLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary mx-auto mb-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mb-0 fs-13">Loading improvement plan details...</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row g-3 mb-3">
                    <div className="col-md-6">
                      <label htmlFor="strengths" className="form-label fw-medium">
                        Strengths <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="strengths"
                        name="strengths"
                        value={formData.strengths}
                        onChange={handleInputChange}
                        placeholder="Describe your key strengths and areas where you excel..."
                        className={`form-control ${errors.strengths ? 'is-invalid' : ''}`}
                        rows={4}
                        disabled={loading}
                      />
                      {errors.strengths && <div className="invalid-feedback">{errors.strengths}</div>}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="weaknesses" className="form-label fw-medium">
                        Weaknesses <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="weaknesses"
                        name="weaknesses"
                        value={formData.weaknesses}
                        onChange={handleInputChange}
                        placeholder="Identify areas for improvement and development..."
                        className={`form-control ${errors.weaknesses ? 'is-invalid' : ''}`}
                        rows={4}
                        disabled={loading}
                      />
                      {errors.weaknesses && <div className="invalid-feedback">{errors.weaknesses}</div>}
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <label htmlFor="employee_comment" className="form-label fw-medium">
                        Comment <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="employee_comment"
                        name="employee_comment"
                        value={formData.employee_comment}
                        onChange={handleInputChange}
                        placeholder="Share your thoughts, concerns, or suggestions about your performance and development..."
                        className={`form-control ${errors.employee_comment ? 'is-invalid' : ''}`}
                        rows={4}
                        disabled={loading}
                      />
                      {errors.employee_comment && <div className="invalid-feedback">{errors.employee_comment}</div>}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="training_needs" className="form-label fw-medium">
                        Training Needs <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="training_needs"
                        name="training_needs"
                        value={formData.training_needs}
                        onChange={handleInputChange}
                        placeholder="Specify the training, courses, or development activities you need..."
                        className={`form-control ${errors.training_needs ? 'is-invalid' : ''}`}
                        rows={4}
                        disabled={loading}
                      />
                      {errors.training_needs && <div className="invalid-feedback">{errors.training_needs}</div>}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary d-inline-flex align-items-center gap-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          <span>Updating...</span>
                        </>
                      ) : (
                        <>
                          <i className="ti ti-device-floppy fs-18" />
                          <span>Update Improvement Plan</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  );
};

export default HREditIP;
